import { useLang } from 'hooks/lang';

import { Team, User, actions, projectStatusByCode, useActiveOwnershipBlock } from 'entities';
import { EntitySelect } from 'components/Entities';

import { Button, Column, Dot, Modal, Row } from 'components/Blocks';
import { RefreshIcon, WarningIcon } from 'components/Icons';
import { useConfirmation } from 'providers/Confirmation';

import { useFormControl } from 'hooks';
import { sortByName } from 'utils/array';

interface ChangeTeamProps {
  children?: never;
  user: User;
  onFinish: () => void;
}

function ChangeTeam({ user, onFinish }: ChangeTeamProps) {
  const { t } = useLang();

  const { state, ...handlers } = useFormControl({
    teams: [] as Team[],
    team: '',
  });

  const { requestConfirmation } = useConfirmation();

  const changeTeam = actions.users.changeUserTeam.use({
    onSuccess: onFinish,
  });

  const [ownershipProjects, changeUser] = useActiveOwnershipBlock(user.id, async (team: string) =>
    changeTeam.execute({ id: user.id, team }),
  );

  const executeChange = (): void => {
    if (!state.team) return;

    const selectedTeam = state.teams.find(({ id }) => id === state.team)?.name;

    requestConfirmation({
      text: `${t('user-will-be-changed')} "${selectedTeam}".`,

      onConfirm: async () => changeUser(state.team),
    });
  };

  return (
    <>
      <Modal
        title={t('change-user-team?') as string}
        width="min(80vw, 450px)"
        trigger={
          <Button loading={changeTeam.loading} className="mx-auto" variant="ghost">
            <RefreshIcon />
            {t('change-team')}
          </Button>
        }
      >
        {ownershipProjects.length ? (
          <>
            <Row justifyContent="center" gap="2" className="text-destructive-light">
              <WarningIcon />

              <p className="text-sm font-semibold">
                {t('user-has-active-ownership')} ({ownershipProjects.length})
              </p>
            </Row>

            <Column gap="2">
              {ownershipProjects.map((project) => (
                <Button
                  key={project.id}
                  variant="link"
                  className="items-start justify-start whitespace-normal text-start text-sm"
                >
                  <Dot
                    color={projectStatusByCode[project.status].color}
                    size={8}
                    className="mt-1.5"
                  />
                  {project.name}
                </Button>
              ))}
            </Column>
          </>
        ) : (
          <>
            <EntitySelect
              target="teams.list"
              id="team"
              value={state.team}
              onChange={handlers.handleChange}
              label={t('team')}
              parser={(data) => sortByName(data.filter(({ id }) => id !== user.team))}
              onLoad={(teams) => handlers.dispatch({ teams })}
              labelKey="name"
            />

            <Button disabled={!state.team} onClick={executeChange}>
              <RefreshIcon />

              {t('change')}
            </Button>
          </>
        )}
      </Modal>
    </>
  );
}

export default ChangeTeam;
